<template>
  <el-dialog
    title="Guest Check-Out"
    :visible.sync="dialogVisible"
    width="700px"
    :before-close="handleClose"
  >
    <div v-if="booking">
      <!-- Guest Information -->
      <el-card class="info-card">
        <div slot="header">
          <span>Guest Information</span>
        </div>
        <el-row :gutter="20">
          <el-col :span="12">
            <p><strong>Name:</strong> {{ booking.guest_name }}</p>
            <p><strong>Room:</strong> {{ booking.room_number }}</p>
            <p><strong>Check-in:</strong> {{ formatDate(booking.check_in_date) }}</p>
          </el-col>
          <el-col :span="12">
            <p><strong>Booking Code:</strong> {{ booking.booking_code }}</p>
            <p><strong>Expected Check-out:</strong> {{ formatDate(booking.check_out_date) }}</p>
            <p><strong>Stay Duration:</strong> {{ stayDuration }} nights</p>
          </el-col>
        </el-row>
      </el-card>

      <!-- Additional Charges -->
      <el-card class="info-card">
        <div slot="header">
          <span>Additional Charges</span>
          <el-button
            type="text"
            size="small"
            @click="addCharge"
            style="float: right; padding: 3px 0"
          >
            <i class="el-icon-plus"></i> Add Charge
          </el-button>
        </div>

        <el-table :data="additionalCharges" style="width: 100%" show-summary :summary-method="getSummaries">
          <el-table-column prop="description" label="Description">
            <template slot-scope="scope">
              <el-input
                v-if="scope.row.editing"
                v-model="scope.row.description"
                size="small"
              ></el-input>
              <span v-else>{{ scope.row.description }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="amount" label="Amount" width="150">
            <template slot-scope="scope">
              <el-input-number
                v-if="scope.row.editing"
                v-model="scope.row.amount"
                :min="0"
                :precision="2"
                size="small"
                controls-position="right"
              ></el-input-number>
              <span v-else>${{ scope.row.amount.toFixed(2) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="120" align="center">
            <template slot-scope="scope">
              <el-button
                v-if="scope.row.editing"
                type="text"
                size="small"
                @click="saveCharge(scope.row)"
              >
                Save
              </el-button>
              <el-button
                v-else
                type="text"
                size="small"
                @click="editCharge(scope.row)"
              >
                Edit
              </el-button>
              <el-button
                type="text"
                size="small"
                @click="removeCharge(scope.$index)"
              >
                Delete
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <!-- Final Bill -->
      <el-card class="info-card">
        <div slot="header">
          <span>Final Bill Summary</span>
        </div>
        <el-row>
          <el-col :span="12">
            <p><strong>Room Charges:</strong> ${{ booking.total_amount.toFixed(2) }}</p>
            <p><strong>Additional Charges:</strong> ${{ totalAdditionalCharges.toFixed(2) }}</p>
            <p><strong>Subtotal:</strong> ${{ subtotal.toFixed(2) }}</p>
            <p><strong>Tax (10%):</strong> ${{ tax.toFixed(2) }}</p>
          </el-col>
          <el-col :span="12">
            <p><strong>Total Amount:</strong> <span class="total-amount">${{ totalAmount.toFixed(2) }}</span></p>
            <p><strong>Paid Amount:</strong> ${{ paidAmount.toFixed(2) }}</p>
            <p><strong>Balance Due:</strong>
              <span :class="{'text-danger': balanceDue > 0, 'text-success': balanceDue <= 0}">
                ${{ Math.abs(balanceDue).toFixed(2) }}
                {{ balanceDue >= 0 ? '' : '(Refund)' }}
              </span>
            </p>
          </el-col>
        </el-row>

        <!-- Payment Collection -->
        <el-divider></el-divider>
        <el-form ref="form" :model="form" label-width="140px">
          <el-form-item label="Payment Amount" v-if="balanceDue > 0">
            <el-input-number
              v-model="form.payment_amount"
              :min="0"
              :max="balanceDue"
              :precision="2"
              controls-position="right"
            >
              <template slot="prepend">$</template>
            </el-input-number>
            <el-select v-model="form.payment_method" placeholder="Payment method" style="margin-left: 10px">
              <el-option label="Cash" value="cash"></el-option>
              <el-option label="Credit Card" value="credit_card"></el-option>
              <el-option label="Debit Card" value="debit_card"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="Check-out Time">
            <el-time-picker
              v-model="form.checkout_time"
              format="HH:mm"
              value-format="HH:mm"
              placeholder="Select time"
            ></el-time-picker>
          </el-form-item>

          <el-form-item label="Room Condition">
            <el-checkbox-group v-model="form.room_condition">
              <el-checkbox label="clean">Clean</el-checkbox>
              <el-checkbox label="no_damage">No Damage</el-checkbox>
              <el-checkbox label="keys_returned">Keys Returned</el-checkbox>
            </el-checkbox-group>
          </el-form-item>

          <el-form-item label="Notes">
            <el-input
              v-model="form.notes"
              type="textarea"
              :rows="2"
              placeholder="Any additional notes"
            ></el-input>
          </el-form-item>
        </el-form>
      </el-card>
    </div>

    <span slot="footer" class="dialog-footer">
      <el-button @click="handleClose">Cancel</el-button>
      <el-button @click="printInvoice" icon="el-icon-printer">Print Invoice</el-button>
      <el-button type="primary" @click="handleCheckOut" :loading="loading">
        Complete Check-Out
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import bookingService from '@/services/booking.service'

export default {
  name: 'CheckOutDialog',
  props: {
    visible: Boolean,
    booking: Object
  },
  data () {
    return {
      dialogVisible: false,
      loading: false,
      additionalCharges: [],
      form: {
        payment_amount: 0,
        payment_method: 'cash',
        checkout_time: new Date().toTimeString().slice(0, 5),
        room_condition: ['clean', 'no_damage', 'keys_returned'],
        notes: ''
      }
    }
  },
  computed: {
    stayDuration () {
      if (this.booking) {
        const checkIn = new Date(this.booking.check_in_date)
        const checkOut = new Date(this.booking.check_out_date)
        return Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24))
      }
      return 0
    },
    totalAdditionalCharges () {
      return this.additionalCharges.reduce((sum, charge) => sum + charge.amount, 0)
    },
    subtotal () {
      return this.booking.total_amount + this.totalAdditionalCharges
    },
    tax () {
      return this.totalAdditionalCharges * 0.1 // Tax only on additional charges
    },
    totalAmount () {
      return this.subtotal + this.tax
    },
    paidAmount () {
      return this.booking.paid_amount
    },
    balanceDue () {
      return this.totalAmount - this.paidAmount
    }
  },
  watch: {
    visible (val) {
      this.dialogVisible = val
      if (val) {
        this.initializeForm()
      }
    },
    dialogVisible (val) {
      this.$emit('update:visible', val)
    }
  },
  methods: {
    initializeForm () {
      this.form.payment_amount = this.balanceDue > 0 ? this.balanceDue : 0
      this.additionalCharges = []
    },

    formatDate (date) {
      return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    },

    addCharge () {
      this.additionalCharges.push({
        description: '',
        amount: 0,
        editing: true
      })
    },

    editCharge (charge) {
      charge.editing = true
    },

    saveCharge (charge) {
      if (!charge.description || charge.amount <= 0) {
        this.$message.warning('Please enter valid description and amount')
        return
      }
      charge.editing = false
    },

    removeCharge (index) {
      this.additionalCharges.splice(index, 1)
    },

    getSummaries (param) {
      const { columns, data } = param
      const sums = []
      columns.forEach((column, index) => {
        if (index === 0) {
          sums[index] = 'Total'
          return
        }
        if (column.property === 'amount') {
          const values = data.map(item => Number(item[column.property]))
          sums[index] = '$' + values.reduce((prev, curr) => {
            return prev + curr
          }, 0).toFixed(2)
        }
      })
      return sums
    },

    handleClose () {
      this.dialogVisible = false
      this.additionalCharges = []
    },

    async printInvoice () {
      try {
        const response = await bookingService.generateInvoice(this.booking.id)
        // Implement actual printing logic here
        this.$message.success('Invoice generated successfully')
      } catch (error) {
        this.$message.error('Failed to generate invoice')
      }
    },

    async handleCheckOut () {
      try {
        this.loading = true

        const checkOutData = {
          ...this.form,
          actual_check_out_time: new Date().toISOString(),
          additional_charges: this.additionalCharges,
          final_amount: this.totalAmount
        }

        // Process check-out
        await bookingService.checkOut(this.booking.id, checkOutData)

        // Process final payment if any
        if (this.form.payment_amount > 0) {
          await bookingService.addPayment(this.booking.id, {
            amount: this.form.payment_amount,
            payment_method: this.form.payment_method
          })
        }

        // Update room status
        // await roomService.updateRoomStatus(this.booking.room_id, 'available')

        this.$emit('checked-out')
        this.handleClose()
      } catch (error) {
        this.$message.error('Failed to complete check-out')
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.info-card {
  margin-bottom: 20px;
}

.info-card p {
  margin: 8px 0;
  color: #606266;
}

.total-amount {
  color: #409EFF;
  font-size: 20px;
  font-weight: bold;
}

.text-danger {
  color: #F56C6C;
  font-weight: bold;
}

.text-success {
  color: #67C23A;
  font-weight: bold;
}
</style>
